import { useContext, useEffect, useRef } from 'react';
import { UNSAFE_NavigationContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { registerKnowledgePopstateGuard as registerNavigationGuard } from 'pages/console/assistant/knowledgeNavigationBoundary';

/** Use the app's pre-router popstate boundary for show-once credentials too. */
export default function useConnectionSecretGuard(pending, unsaved, requestExit, hasSecret = unsaved) {
  const { t } = useTranslation();
  const context = useContext(UNSAFE_NavigationContext);
  const navigator = context?.navigator;
  const latest = useRef({ pending, unsaved, requestExit, hasSecret, t });
  latest.current = { pending, unsaved, requestExit, hasSecret, t };
  useEffect(() => {
    const originals = {};
    const wrapped = {};
    const index = { value: window.history.state?.idx };
    if (navigator) {
      for (const name of ['push', 'replace', 'go']) {
        originals[name] = navigator[name];
        wrapped[name] = (...args) => {
          const proceed = () => originals[name].apply(navigator, args);
          if (latest.current.pending || latest.current.hasSecret) latest.current.requestExit(proceed);
          else proceed();
        };
        navigator[name] = wrapped[name];
      }
    }
    const unload = (event) => {
      if (!latest.current.pending && !latest.current.unsaved) return;
      event.preventDefault();
      event.returnValue = '';
    };
    let restoring = false;
    const pop = (event) => {
      const next = event.state?.idx;
      if (restoring) {
        restoring = false;
        index.value = next;
        event.stopImmediatePropagation();
        return;
      }
      if ((latest.current.pending || latest.current.unsaved)
        && Number.isInteger(index.value) && Number.isInteger(next) && next !== index.value) {
        const leave = !latest.current.pending && window.confirm(latest.current.t('connections.unsavedBody'));
        if (!leave) {
          event.stopImmediatePropagation();
          restoring = true;
          window.history.go(index.value - next);
          return;
        }
      }
      index.value = next;
    };
    window.addEventListener('beforeunload', unload);
    const unregister = registerNavigationGuard(pop);
    return () => {
      if (navigator) for (const name of Object.keys(originals)) {
        if (navigator[name] === wrapped[name]) navigator[name] = originals[name];
      }
      window.removeEventListener('beforeunload', unload);
      unregister();
    };
  }, [navigator]);
}
